import { faRobot } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../../components/button";
import FitnessToolsResultCard from "../../../components/cards/FitnessToolsResultCard";
import Footer from "../../../components/footer";
import Header from "../../../components/header";
import Input from "../../../components/input";
import Loader from "../../../components/loader";
import OtherFitnessTestSection from "../../../components/OtherFitnessTestSection";
import { useTranslate } from "../../../context/TranslationContext";
import useChartPeriod from "../../../hooks/useChartPeriod";
import {
  useCreateFlexibilityMutation,
  useGetFlexibilityQuery,
  useUpdateFlexibilityMutation,
} from "../../../redux/Apis/FitnessTest/Flexibility";
import { useCreateAISuggestionMutation } from "../../../redux/Apis/TrackLecture";
import { useStaticData } from "../../../utils/data";
import { formatAISuggestion, setMetaTags } from "../../../utils/helper";
import AISkeleton from "../../../components/skeleton/AISkeleton";

const FlexibilityTest = () => {
  const { t } = useTranslate("flexibilityTest");
  const { FITNESSTEST, CALCULATOR_DATA } = useStaticData();
  const lang = useSelector((state) => state.language.lang);
  const location = useLocation();
  const { control, getValues, watch } = useForm({
    defaultValues: {
      method: "sit",
      gender: "male",
    },
  });

  const method = watch("method");
  const [result, setResult] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.authSlice);
  const [aiSuggestion, setAiSuggestion] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [highlightAI, setHighlightAI] = useState(false);
  const aiRef = useRef(null);
  const [createAISuggestion] = useCreateAISuggestionMutation();
  const [createFlexibility] = useCreateFlexibilityMutation();
  const [updateFlexibility] = useUpdateFlexibilityMutation();
  const {
    data: flexibilityHistoryData,
    refetch: refetchFlexibility,
    isLoading,
  } = useGetFlexibilityQuery({ lang }, { skip: !user });
  const { period, dateRange, handlePeriodChange } = useChartPeriod();
  const { startDate, endDate } = dateRange;
  const [selectedFlexibility, setSelectedFlexibility] = useState(
    CALCULATOR_DATA.FLEXIBILITY[0].value,
  );

  const {
    data: flexibilityData,
    refetch: refetchFlexibilityData,
    isLoading: loadingFlexibility,
  } = useGetFlexibilityQuery(
    {
      method: selectedFlexibility,
      startdate: startDate,
      enddate: endDate,
      lang,
    },
    { skip: !startDate || !endDate || !user },
  );

  const courseState = location?.state || {};
  // const templateId = courseState?.templateId;
  const courseId = courseState?.course;
  const sectionId = courseState?.sectionId || courseState?.section;
  const [showSkeleton, setShowSkeleton] = useState(false);

  useEffect(() => {
    if (aiLoading) {
      const timer = setTimeout(() => setShowSkeleton(true), 200);
      return () => clearTimeout(timer);
    } else {
      setShowSkeleton(false);
    }
  }, [aiLoading]);

  useEffect(() => {
    if ((aiLoading || aiSuggestion) && aiRef.current) {
      setTimeout(() => {
        const y =
          aiRef.current.getBoundingClientRect().top + window.pageYOffset - 110; // header height offset

        window.scrollTo({
          top: y,
          behavior: "smooth",
        });

        setHighlightAI(true);
        setTimeout(() => setHighlightAI(false), 3000);
      }, 300);
    }
  }, [aiLoading, aiSuggestion]);

  useEffect(() => {
    if (user) {
      refetchFlexibility();
    }
  }, [user, refetchFlexibility]);

  useEffect(() => {
    if (startDate && endDate && selectedFlexibility && user) {
      refetchFlexibilityData();
    }
  }, [startDate, endDate, selectedFlexibility, user, refetchFlexibilityData]);

  useEffect(() => {
    setMetaTags(
      "Traineego | Flexibility Test",
      "Evaluate your flexibility with Sit-and-Reach or Shoulder Clasp assessments, adjusted for your age and gender.",
    );
  }, []);

  useEffect(() => {
    if (flexibilityHistoryData) {
      setHistoryData(flexibilityHistoryData.data || []);
    }
  }, [flexibilityHistoryData]);

  const onCalculate = () => {
    const { gender, age, method, distance } = getValues();
    const parsedAge = parseInt(age);
    const parsedDistance = parseFloat(distance);

    if (!gender || !age || !method || isNaN(parsedDistance) || parsedAge <= 0) {
      toast.warn(t("messages.enterValidValues"));
      return;
    }

    let interpretation = "";

    if (method === "sit") {
      interpretation = getSitAndReachResult(gender, parsedAge, parsedDistance);
    } else {
      interpretation = getShoulderClaspResult(
        gender,
        parsedAge,
        parsedDistance,
      );
    }

    setResult({
      value: parsedDistance,
      interpretation,
      unit: "cm",
      method,
    });
    toast.success(t("messages.calculateSuccess"));
    return interpretation;
  };

  const getSitAndReachResult = (gender, age, distance) => {
    const baseThresholds = {
      male: [
        { label: t("thresholdLabels.excellent"), min: 35 },
        { label: t("thresholdLabels.good"), min: 30 },
        { label: t("thresholdLabels.average"), min: 21 },
        { label: t("thresholdLabels.belowaverage"), min: 11 },
        { label: t("thresholdLabels.poor"), min: 0 },
      ],
      female: [
        { label: t("thresholdLabels.excellent"), min: 40 },
        { label: t("thresholdLabels.good"), min: 33 },
        { label: t("thresholdLabels.average"), min: 24 },
        { label: t("thresholdLabels.belowaverage"), min: 15 },
        { label: t("thresholdLabels.poor"), min: 0 },
      ],
    };

    let ageAdjust = 0;
    if (age >= 30 && age <= 39) ageAdjust = 2;
    else if (age <= 49) ageAdjust = 4;
    else if (age <= 59) ageAdjust = 6;
    else if (age <= 69) ageAdjust = 8;
    else if (age >= 70) ageAdjust = 10;

    const adjusted = baseThresholds[gender.toLowerCase()].map((t) => ({
      ...t,
      min: t.min - ageAdjust,
    }));

    return adjusted.find((t) => distance >= t.min)?.label || "Unknown";
  };

  const getShoulderClaspResult = (gender, age, gap) => {
    const baseThresholds = {
      male: [
        { label: t("thresholdLabels.excellent"), max: 0 },
        { label: t("thresholdLabels.good"), max: 5 },
        { label: t("thresholdLabels.average"), max: 10 },
        { label: t("thresholdLabels.belowaverage"), max: 15 },
        { label: t("thresholdLabels.poor"), max: Infinity },
      ],
      female: [
        { label: t("thresholdLabels.excellent"), max: 0 },
        { label: t("thresholdLabels.good"), max: 4 },
        { label: t("thresholdLabels.average"), max: 8 },
        { label: t("thresholdLabels.belowaverage"), max: 12 },
        { label: t("thresholdLabels.poor"), max: Infinity },
      ],
    };

    let ageAdjust = 0;
    if (age >= 30 && age <= 39) ageAdjust = 1;
    else if (age <= 49) ageAdjust = 2;
    else if (age <= 59) ageAdjust = 3;
    else if (age <= 69) ageAdjust = 4;
    else if (age >= 70) ageAdjust = 5;

    const adjusted = baseThresholds[gender.toLowerCase()].map((t) => ({
      ...t,
      max: t.max + ageAdjust,
    }));

    return adjusted.find((t) => gap <= t.max)?.label || "Unknown";
  };

  const generateAICoach = async (inputResult) => {
    try {
      // if (!courseId || !sectionId) return;

      setAiLoading(true);

      const aiPayload = {
        courseid: courseId,
        sectionid: sectionId,
        learnerid: user._id,
        type: "Flexibility Test",
        result: [
          {
            gender: inputResult.gender,
            age: inputResult.age,
            method: inputResult.method,
            reachdistance: inputResult.reachdistance || null,
            shoulderclasp: inputResult.shoulderclasp || null,
            result: inputResult.result,
          },
        ],
      };

      const res = await createAISuggestion({
        payload: aiPayload,
        params: { lang },
      }).unwrap();

      const suggestion = res?.data?.suggestion;
      if (suggestion) setAiSuggestion(suggestion);
    } catch (err) {
      console.log("AI error", err);
    } finally {
      setAiLoading(false);
    }
  };

  const handleSaveResult = async (type, resultData) => {
    if (!resultData?.result) {
      toast.warn(t("messages.calculateFirst"));
      return;
    }

    if (!user) {
      toast.warn(t("messages.loginFirst"));
      // navigate("/auth/login");
      return;
    }

    try {
      const { gender, age, method, distance } = getValues();
      const payload = {
        gender,
        age,
        method,
        result: resultData.result,
      };
      if (courseId) payload.course = courseId;
      if (method === "sit") payload.reachdistance = distance;
      else if (method === "shoulder") payload.shoulderclasp = distance;

      const response = await createFlexibility({
        payload,
        params: { lang },
      }).unwrap();
      toast.success(response.message || t("messages.saveSuccess"));
      refetchFlexibility();
      refetchFlexibilityData();
      // if (courseId && sectionId && templateId) {
      await generateAICoach(payload);
      // }
    } catch (error) {
      toast.error(t("messages.saveError"));
    }
  };

  const handleDeleteLast = async () => {
    try {
      const latestEntry = [...historyData].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      )[0];
      if (latestEntry) {
        await updateFlexibility({
          id: latestEntry._id,
          isactive: false,
          params: { lang },
        }).unwrap();
        toast.success(t("messages.deleteSuccess"));
        refetchFlexibility();
        refetchFlexibilityData();
      }
    } catch (error) {
      toast.error(t("messages.deleteError"));
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return t("messages.notCalculated");
    return new Date(timestamp).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatHistoryEntry = (entry) => {
    return (
      <div className="d-flex justify-content-between align-items-center">
        <p className="mb-0">{formatDateTime(entry.createdAt)}</p>
        <p className="mb-0 fw-bold">
          {entry.reachdistance || entry.shoulderclasp} cm - {entry.result}
        </p>
      </div>
    );
  };

  const toggleHistory = () => {
    setShowHistory((prev) => !prev);
  };

  if (isLoading || loadingFlexibility) {
    return <Loader loading={true} />;
  }

  const handleFlexibilityTabChangeFactory = (setSelectedFlexibility) => {
    return (index) => {
      const selected = CALCULATOR_DATA.FLEXIBILITY[index].value;
      setSelectedFlexibility(selected);
    };
  };

  const handleFlexibilityTabChange = handleFlexibilityTabChangeFactory(
    setSelectedFlexibility,
  );

  return (
    <>
      <Header />
      <section className="banner" style={{ textAlign: "left" }}>
        <div className="container py-5">
          <div className="row">
            <div className="col-12 text-center">
              <h1 className="text-uppercase">{t("title")}</h1>
              <p className="mb-0 p-lg l-grey-text">{t("subtitle")}</p>
            </div>
          </div>

          <div className="row d-flex">
            <div
              className="col-md-12 mt-4 p-5"
              style={{ backgroundColor: "#161617", borderRadius: "2rem" }}
            >
              <div className="row">
                <div className="col-lg-8">
                  <h4 className="text-uppercase mb-4">{t("testTitle")}</h4>
                  <div className="row">
                    <div className="col-md-6">
                      <Controller
                        name="gender"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label={t("inputs.gender")}
                            type="select"
                            options={[
                              { value: "male", label: t("inputs.male") },
                              { value: "female", label: t("inputs.female") },
                            ]}
                            {...field}
                          />
                        )}
                      />
                    </div>
                    <div className="col-md-6">
                      <Controller
                        name="age"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label={t("inputs.age")}
                            type="number"
                            placeholder={t("inputs.agePlaceholder")}
                            {...field}
                          />
                        )}
                      />
                    </div>
                    <div className="col-md-6 mt-3">
                      <Controller
                        name="method"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label={t("inputs.method")}
                            type="select"
                            options={[
                              { value: "sit", label: t("inputs.sitAndReach") },
                              {
                                value: "shoulder",
                                label: t("inputs.shoulderClasp"),
                              },
                            ]}
                            {...field}
                          />
                        )}
                      />
                    </div>

                    <div className="col-md-6 mt-3">
                      <Controller
                        name="distance"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label={
                              method === "sit"
                                ? t("inputs.reachDistance")
                                : t("inputs.gapDistance")
                            }
                            type="number"
                            placeholder={
                              method === "sit"
                                ? t("inputs.reachPlaceholder")
                                : t("inputs.gapPlaceholder")
                            }
                            tooltip={
                              method === "shoulder"
                                ? t("inputs.shoulderTooltip")
                                : undefined
                            }
                            {...field}
                          />
                        )}
                      />
                    </div>
                  </div>

                  <div className="row mt-4">
                    <div
                      className="col-12 bg-white py-2 px-4 d-flex justify-content-between align-items-center"
                      style={{ borderRadius: "0.5rem" }}
                    >
                      <p className="grey-text mb-0">{t("calculatePrompt")}</p>
                      <Button
                        text={t("calculateBtn")}
                        type="button"
                        onClick={onCalculate}
                      />
                    </div>
                  </div>
                  {result && location.state?.returnTo && (
                    <div className="row mt-4">
                      <div className="col-12 text-center">
                        <Button
                          text="Continue Learning"
                          className="site-btn border-btn"
                          style={{ width: "100%", maxWidth: "200px" }}
                          onClick={() => {
                            navigate(location.state.returnTo, {
                              state: {
                                ...location.state,
                                resultKey: `${location.state.sectionIndex}-${location.state.lectureIndex}-FLEXIBILITY`,
                                resultData: {
                                  type: "FLEXIBILITY",
                                  result: `${result.interpretation}`,
                                  interpretation: result.interpretation,
                                  value: result.value,
                                  timestamp: new Date().toISOString(),
                                },
                              },
                            });
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="col-12 col-lg-4 mt-4 mt-lg-0">
                  <div
                    className="h-100 p-3 p-md-4 d-flex flex-column justify-content-center"
                    style={{
                      border: "1px solid white",
                      borderRadius: "1rem",
                      background: "rgba(112,112,112,0.4)",
                    }}
                  >
                    <h6>
                      {result
                        ? `${method === "sit" ? "Reach Distance" : "Gap"}: ${
                            result.value
                          } cm – ${result.interpretation} ${t(
                            "results.displayResult",
                          )}`
                        : t("results.placeholder")}
                    </h6>

                    <p className="p-sm">{t("results.info")}</p>
                    <div
                      className="bg-white w-100 p-3 text-center mt-3"
                      style={{
                        border: "0.5px solid gray",
                        borderRadius: "0.5rem",
                      }}
                    >
                      <p className="m-0 grey-text p-sm">{t("results.tip")}</p>
                      <Button text={t("anotherTestBtn")} to="/fitness-test" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <FitnessToolsResultCard
              type="Flexibility"
              result={
                result
                  ? {
                      result: result.interpretation,
                      timestamp: new Date().toISOString(),
                    }
                  : null
              }
              historyData={historyData}
              showHistory={showHistory}
              toggleHistory={toggleHistory}
              handleSaveResult={handleSaveResult}
              handleDeleteLast={handleDeleteLast}
              formatHistoryEntry={formatHistoryEntry}
              formatDateTime={formatDateTime}
              chartData={flexibilityData?.data || []}
              period={period}
              handlePeriodChange={handlePeriodChange}
              selectedFlexibility={selectedFlexibility}
              handleFlexibilityTabChange={handleFlexibilityTabChange}
            />

            {(aiLoading || aiSuggestion) && (
              <div
                className={`ai-suggestion-box ${highlightAI ? "ai-highlight" : ""}`}
                ref={aiRef}
              >
                <h4 className="text-white mb-3">
                  {/* <FontAwesomeIcon icon={faRobot} color="var(--primaryColor)" />{" "} */}
                  <img
                    src="https://traineego.com/assets/images/favicon-2026.ico"
                    alt="AI Coach"
                    style={{
                      width: "45px",
                      marginRight: "8px",
                      borderRadius: "25px",
                    }}
                  />
                  {t("aiSuggestion.title")}
                </h4>

                {showSkeleton ? (
                  // <p style={{ color: "#aaa" }}>
                  //   <Spinner size="sm" /> {t("aiSuggestion.loading")}
                  // </p>
                  <AISkeleton />
                ) : (
                  <div
                    style={{ color: "#d1d1d6", lineHeight: "1.7" }}
                    dangerouslySetInnerHTML={{
                      __html: formatAISuggestion(aiSuggestion),
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </section>
      <OtherFitnessTestSection
        data={FITNESSTEST.filter(
          (item) => item.route !== window.location.pathname,
        )}
      />
      <Footer />
    </>
  );
};

export default FlexibilityTest;
